
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  MessageCircle,
  Mail,
  ChevronLeft,
  ChevronRight,
  Facebook,
  Linkedin,
  Instagram,
  Twitter,
  Send,
} from 'lucide-react';

const CardPreview = ({ data, theme }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // If no theme selected
  if (!theme) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed #ccc',
          borderRadius: 16,
          background: '#f9fafb',
          color: '#6b7280',
        }}
      >
        Select a theme to preview your card
      </div>
    );
  }

  // Media items
  const mediaItems = [
    ...(data.image1 ? [{ type: 'image', url: data.image1 }] : []),
    ...(data.image2 ? [{ type: 'image', url: data.image2 }] : []),
    ...(data.image3 ? [{ type: 'image', url: data.image3 }] : []),
    ...(data.image4 ? [{ type: 'image', url: data.image4 }] : []),
    ...(data.image5 ? [{ type: 'image', url: data.image5 }] : []),
    ...(data.image6 ? [{ type: 'image', url: data.image6 }] : []),
    ...(data.image7 ? [{ type: 'image', url: data.image7 }] : []),
    ...(data.image8 ? [{ type: 'image', url: data.image8 }] : []),
    ...(data.image9 ? [{ type: 'image', url: data.image9 }] : []),
    ...(data.image10 ? [{ type: 'image', url: data.image10 }] : []),
    ...(data.video1 ? [{ type: 'video', url: data.video1 }] : []),
    ...(data.video2 ? [{ type: 'video', url: data.video2 }] : []),
  ];

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length
    );
  };

  // Social links
  const socialLinks = [
    { icon: Facebook, url: data.facebook, color: '#1877F2' },
    { icon: Linkedin, url: data.linkedin, color: '#0A66C2' },
    { icon: Instagram, url: data.instagram, color: '#E4405F' },
    { icon: Twitter, url: data.twitter, color: '#1DA1F2' },
    { icon: Send, url: data.telegram, color: '#0088CC' },
  ].filter((item) => item.url);

  return (
    <div
      style={{
        maxWidth: 380,
        margin: '0 auto',
        borderRadius: 24,
        overflow: 'hidden',
        background: theme.colors.cardBg,
        border: `2px solid ${theme.colors.border}`,
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Logo */}
        {data.companyLogo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <img
              src={data.companyLogo}
              alt="Logo"
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                objectFit: 'cover',
                border: `3px solid ${theme.colors.primary}`,
              }}
            />
          </motion.div>
        )}

        {/* Name */}
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ margin: 0, color: theme.colors.text }}>
            {data.name || 'Your Name'}
          </h2>
          <p style={{ margin: 4, color: theme.colors.textSecondary }}>
            {data.designation || 'Your Designation'}
          </p>
          {data.companyName && (
            <p style={{ margin: 0, fontWeight: 'bold', color: theme.colors.primary }}>
              {data.companyName}
            </p>
          )}
        </div>

        {/* Media */}
        {mediaItems.length > 0 && (
          <div style={{ position: 'relative' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMediaIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                style={{
                  height: 180,
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: `2px solid ${theme.colors.border}`,
                }}
              >
                {mediaItems[currentMediaIndex].type === 'image' ? (
                  <img
                    src={mediaItems[currentMediaIndex].url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      color: theme.colors.textSecondary,
                    }}
                  >
                    Video Preview
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {mediaItems.length > 1 && (
              <>
                <button
                  onClick={prevMedia}
                  style={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    cursor: 'pointer',
                    background: theme.colors.buttonBg,
                  }}
                >
                  <ChevronLeft color={theme.colors.buttonText} size={18} />
                </button>

                <button
                  onClick={nextMedia}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    cursor: 'pointer',
                    background: theme.colors.buttonBg,
                  }}
                >
                  <ChevronRight color={theme.colors.buttonText} size={18} />
                </button>
              </>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {data.callLink && (
            <a
              href={`tel:${data.callLink}`}
              style={{
                padding: 12,
                borderRadius: theme.styles.buttonRadius,
                background: theme.colors.buttonBg,
                color: theme.colors.buttonText,
                textAlign: 'center',
                textDecoration: 'none',
                fontWeight: 'bold',
              }}
            >
              <Phone size={16} /> Call
            </a>
          )}

          {data.whatsappLink && (
            <a
              href={data.whatsappLink}
              target="_blank"
              rel="noreferrer"
              style={{
                padding: 12,
                borderRadius: theme.styles.buttonRadius,
                background: theme.colors.buttonBg,
                color: theme.colors.buttonText,
                textAlign: 'center',
                textDecoration: 'none',
                fontWeight: 'bold',
              }}
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          )}
        </div>

        {/* Email */}
        {data.email && (
          <a
            href={`mailto:${data.email}`}
            style={{
              padding: 12,
              borderRadius: theme.styles.buttonRadius,
              background: theme.colors.buttonBg,
              color: theme.colors.buttonText,
              textAlign: 'center',
              textDecoration: 'none',
              fontWeight: 'bold',
            }}
          >
            <Mail size={16} /> {data.email}
          </a>
        )}

        {/* Social */}
        {socialLinks.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
            {socialLinks.map((item, i) => (
              <a
                key={i}
                href={item.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: item.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <item.icon color="#fff" size={18} />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPreview;
